<script>
    import { createEventDispatcher } from "svelte";
    import { Section, Button } from "../../lib/ui";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
    import { EFFECTS, effectOf, SKILL_DURATION, SKILL_SLOT_KINDS, SKILL_WEIGHT, SLOT_COUNT, newSkill } from "./gameSlots.js";

    export let bot;
    /** { items: [저작 스킬], drops: [떨어뜨릴 스킬 id] } */
    export let skills = { items: [], drops: [] };
    /** assetId → URL. 썸네일용(저장하지 않는다). */
    export let urls = {};

    const dispatch = createEventDispatcher();

    let pickerOpen = false;
    let pickerTarget = null; // { index, field }

    $: items = Array.isArray(skills?.items) ? skills.items : [];
    $: drops = Array.isArray(skills?.drops) ? skills.drops : [];

    const emit = (next) => dispatch("change", next);
    const emitItems = (nextItems) => emit({ ...skills, items: nextItems });

    const updateAt = (index, patch) =>
        emitItems(items.map((s, i) => (i === index ? { ...s, ...patch } : s)));

    const addSkill = () => {
        const s = newSkill();
        // 만들자마자 떨어지게 해 둔다 — 스킬을 만드는 이유가 대개 그거라서.
        emit({ ...skills, items: [...items, s], drops: [...drops, s.id] });
    };

    const removeSkill = (index) => {
        const gone = items[index];
        emit({
            ...skills,
            items: items.filter((_, i) => i !== index),
            // ⚠️ 드롭 후보에서도 빼야 한다. 안 그러면 떨어뜨릴 게 없는 id 가 남는다
            //    (서버·런타임이 걸러주긴 하지만, 빌더가 거짓 상태를 들고 있게 된다).
            drops: drops.filter((id) => id !== gone?.id),
        });
    };

    const move = (index, dir) => {
        const to = index + dir;
        if (to < 0 || to >= items.length) return;
        const next = [...items];
        [next[index], next[to]] = [next[to], next[index]];
        emitItems(next);
    };

    const toggleDrop = (id) =>
        emit({
            ...skills,
            drops: drops.includes(id) ? drops.filter((d) => d !== id) : [...drops, id],
        });

    /**
     * 효과를 바꾸면 세기(power)도 같이 비운다 — 효과마다 뜻이 다른 값이라
     * '남는 속도 0.35' 가 '점수 ×0.35' 로 둔갑하면 안 된다(런타임이 범위로 막긴 한다).
     */
    const changeEffect = (index, value) => updateAt(index, { effect: value, power: null });

    const openPicker = (index, field) => {
        pickerTarget = { index, field };
        pickerOpen = true;
    };

    const onPick = (e) => {
        const { assetId, url } = e.detail;
        pickerOpen = false;
        if (!pickerTarget) return;
        const { index, field } = pickerTarget;
        pickerTarget = null;
        dispatch("assetUrl", { assetId, url });
        updateAt(index, { [field]: assetId });
    };

    const clearSlot = (index, field) => updateAt(index, { [field]: null });

    const num = (v, def) => (typeof v === "number" && Number.isFinite(v) ? v : def);
    const durationOf = (s) => num(s.durationMs, SKILL_DURATION.def);
    const weightOf = (s) => num(s.weight, SKILL_WEIGHT.def);
    const powerOf = (s, spec) => num(s.power, spec.def);
    const hintFor = (s, kind) =>
        kind.field === "effectAssetId" ? effectOf(s.effect).effectHint : kind.hint;
</script>

<Section
    title="스킬"
    description="내장 효과 하나에 이름과 그림을 입혀 만듭니다. 효과가 같아도 그림이 다르면 다른 스킬입니다."
>
    <p class="tw-mb-2 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        만든 스킬은 두 군데에 쓰입니다 — <strong class="tw-text-foreground">적이 떨어뜨리는 아이템</strong>(먹으면
        화면 왼쪽 아래 칸에 담기고, 눌러서 씁니다)과
        <strong class="tw-text-foreground">캐릭터 스킬</strong>([캐릭터] 탭에서 캐릭터마다 고릅니다).
        같은 스킬을 양쪽에 써도 됩니다.
    </p>
    <p class="tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        그림은 전부 선택입니다. 비워두면 내장 그래픽으로 나오고 게임은 똑같이 동작합니다 —
        스킬의 움직임·세기는 이미지와 무관합니다.
    </p>

    {#if items.length === 0}
        <div class="tw-mt-4 tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-muted/40 tw-p-5">
            <div class="tw-mb-1 tw-text-sm tw-font-bold tw-text-foreground">아직 만든 스킬이 없어요</div>
            <div class="tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
                지금은 내장 기본 스킬로 게임이 돌아갑니다. 하나라도 만들면 그때부터는
                <strong class="tw-text-foreground">여기 만든 것만</strong> 나옵니다.
            </div>
        </div>
    {:else if drops.length === 0}
        <div class="tw-mt-4 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
            떨어지게 해 둔 스킬이 하나도 없습니다. 게임 중에 아이템이 안 나옵니다
            (캐릭터 스킬로는 그대로 쓸 수 있어요).
        </div>
    {:else if drops.length > SLOT_COUNT}
        <div class="tw-mt-4 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
            화면 아래 아이템 칸이 <strong>{SLOT_COUNT}개</strong>라, 떨어지는 건
            <strong>위에서부터 {SLOT_COUNT}개</strong>뿐입니다 — 지금은
            <strong>{drops.slice(0, SLOT_COUNT).map((id) => items.find((s) => s.id === id)?.name || "이름 없음").join(", ")}</strong>.
            칸에 스킬을 미리 배정해 두어야 게임 시작부터 아이콘이 보이고, 담을 칸 없는 아이템이
            떨어져 그냥 사라지는 일이 없습니다. 순서는 ↑↓ 로 바꾸고, 맵마다 다르게 쓰려면
            [맵] 탭에서 맵별로 고르세요.
        </div>
    {/if}
</Section>

{#each items as s, i (s.id)}
    {@const eff = effectOf(s.effect)}
    <Section>
        <div class="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span class="tw-h-3 tw-w-3 tw-shrink-0 tw-rounded-full" style="background:{eff.color}"></span>
            <input
                class="tw-h-9 tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                placeholder="스킬 이름 (예: 소금폭탄)"
                value={s.name || ""}
                on:change={(e) => updateAt(i, { name: e.target.value })}
            />
            <button
                type="button"
                class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground"
                title="위로" on:click={() => move(i, -1)}>↑</button
            >
            <button
                type="button"
                class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground"
                title="아래로" on:click={() => move(i, 1)}>↓</button
            >
            <button
                type="button"
                class="tw-h-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-xs tw-text-muted-foreground hover:tw-border-destructive hover:tw-text-destructive"
                on:click={() => removeSkill(i)}>삭제</button
            >
        </div>

        <div class="tw-flex tw-flex-col tw-gap-5 lg:tw-flex-row lg:tw-gap-6">
            <!-- 그림 3칸 -->
            <div class="tw-flex tw-shrink-0 tw-flex-wrap tw-gap-4">
                {#each SKILL_SLOT_KINDS as kind (kind.field)}
                    <div class="tw-flex tw-w-[108px] tw-flex-col tw-items-center tw-gap-1">
                        <button
                            type="button"
                            class="slot-thumb {s[kind.field] && urls[s[kind.field]]
                                ? 'tw-border-border'
                                : 'tw-border-dashed tw-border-border'}"
                            on:click={() => openPicker(i, kind.field)}
                        >
                            {#if s[kind.field] && urls[s[kind.field]]}
                                <img crossorigin="anonymous" class="tw-max-h-full tw-max-w-full tw-object-contain" src={urls[s[kind.field]]} alt="" />
                            {:else}
                                <span class="tw-text-2xl tw-text-muted-foreground">+</span>
                            {/if}
                        </button>
                        <div class="tw-text-[13px] tw-font-semibold tw-text-foreground">{kind.label}</div>
                        {#if s[kind.field]}
                            <button
                                type="button"
                                class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                                on:click={() => clearSlot(i, kind.field)}>기본으로</button
                            >
                        {:else if hintFor(s, kind)}
                            <div class="tw-text-center tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                                {hintFor(s, kind)}
                            </div>
                        {/if}
                    </div>
                {/each}
            </div>

            <!-- 효과 · 지속 · 세기 · 드롭 -->
            <div class="tw-min-w-0 tw-flex-1">
                <label class="tw-mb-4 tw-flex tw-flex-col tw-gap-1.5">
                    <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">효과</span>
                    <select
                        class="tw-h-9 tw-w-[200px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                        value={s.effect}
                        on:change={(e) => changeEffect(i, e.target.value)}
                    >
                        {#each EFFECTS as e (e.value)}
                            <option value={e.value}>{e.label}</option>
                        {/each}
                    </select>
                    <span class="tw-text-xs tw-text-muted-foreground">{eff.desc}</span>
                </label>

                {#if !eff.instant}
                    <div class="tw-mb-4">
                        <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <span class="tw-text-[13px] tw-text-foreground">지속 시간</span>
                            <span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold {s.durationMs != null ? 'tw-text-primary' : 'tw-text-muted-foreground'}">
                                {(durationOf(s) / 1000).toFixed(1)}초
                                {#if s.durationMs != null}
                                    <button
                                        type="button"
                                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-normal tw-text-muted-foreground tw-underline"
                                        on:click={() => updateAt(i, { durationMs: null })}>기본으로</button
                                    >
                                {/if}
                            </span>
                        </div>
                        <input
                            class="tw-w-full"
                            style="accent-color: hsl(var(--sb-primary))"
                            type="range"
                            min={SKILL_DURATION.min}
                            max={SKILL_DURATION.max}
                            step={SKILL_DURATION.step}
                            value={durationOf(s)}
                            on:change={(e) => updateAt(i, { durationMs: Number(e.target.value) })}
                        />
                    </div>
                {/if}

                {#if eff.power}
                    <div class="tw-mb-4">
                        <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <span class="tw-text-[13px] tw-text-foreground">{eff.power.label}</span>
                            <span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold {s.power != null ? 'tw-text-primary' : 'tw-text-muted-foreground'}">
                                {powerOf(s, eff.power)}
                                {#if s.power != null}
                                    <button
                                        type="button"
                                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-normal tw-text-muted-foreground tw-underline"
                                        on:click={() => updateAt(i, { power: null })}>기본으로</button
                                    >
                                {/if}
                            </span>
                        </div>
                        <input
                            class="tw-w-full"
                            style="accent-color: hsl(var(--sb-primary))"
                            type="range"
                            min={eff.power.min}
                            max={eff.power.max}
                            step={eff.power.step}
                            value={powerOf(s, eff.power)}
                            on:change={(e) => updateAt(i, { power: Number(e.target.value) })}
                        />
                        <div class="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">{eff.power.hint}</div>
                    </div>
                {/if}

                <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                    <input
                        type="checkbox"
                        style="accent-color: hsl(var(--sb-primary))"
                        checked={drops.includes(s.id)}
                        on:change={() => toggleDrop(s.id)}
                    />
                    <span class="tw-text-[13px] tw-text-foreground">적을 잡으면 떨어지게 하기</span>
                </label>
                <div class="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
                    떨어질 <strong class="tw-text-foreground">확률</strong>은 [난이도] 탭(전역)과 [맵] 탭(맵별 배율)에서,
                    <strong class="tw-text-foreground">어느 맵에</strong> 나올지는 [맵] 탭에서 정합니다.
                </div>

                {#if drops.includes(s.id)}
                    <div class="tw-mt-3">
                        <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <span class="tw-text-[13px] tw-text-foreground">떨어지는 비중</span>
                            <span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold {s.weight != null ? 'tw-text-primary' : 'tw-text-muted-foreground'}">
                                ×{weightOf(s)}
                                {#if s.weight != null}
                                    <button
                                        type="button"
                                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-normal tw-text-muted-foreground tw-underline"
                                        on:click={() => updateAt(i, { weight: null })}>기본으로</button
                                    >
                                {/if}
                            </span>
                        </div>
                        <input
                            class="tw-w-full"
                            style="accent-color: hsl(var(--sb-primary))"
                            type="range"
                            min={SKILL_WEIGHT.min}
                            max={SKILL_WEIGHT.max}
                            step={SKILL_WEIGHT.step}
                            value={weightOf(s)}
                            on:change={(e) => updateAt(i, { weight: Number(e.target.value) })}
                        />
                        <div class="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
                            비중 1짜리 대비 몇 배로 자주 나오는지. 아이템이 <strong class="tw-text-foreground">얼마나 자주 떨어지는지</strong>와는 다른 축입니다.
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </Section>
{/each}

<div>
    <Button variant="outline" on:click={addSkill}>+ 새 스킬 만들기</Button>
</div>

<ImagePicker
    bind:open={pickerOpen}
    chatId={bot.id}
    contextFolder={botAssetFolder(bot)}
    mode="single"
    currentAssetId={pickerTarget ? items[pickerTarget.index]?.[pickerTarget.field] : null}
    on:select={onPick}
/>

<style>
    /* 투명 PNG 가 대부분이라 밝은 바탕에선 안 보인다 — 게임 화면과 비슷한 어두운 바탕에 둔다. */
    .slot-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 108px;
        height: 108px;
        padding: 0;
        border-width: 1px;
        border-style: solid;
        border-radius: 12px;
        background: #0d1430;
        cursor: pointer;
        overflow: hidden;
    }

    .slot-thumb:hover {
        border-color: hsl(var(--sb-primary));
    }
</style>
